import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const EditEpisodeModal = ({ isOpen, episode, seasons, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    episodeNumber: '',
    runtime: '',
    description: '',
    videoUrl: '',
    poster: '',
    srtUrl: '',
    fileSize: '',
    mimeType: '',
    seasonId: ''
  });

  // Cuando el episodio cambia, actualizar el formulario
  useEffect(() => {
    if (episode) {
      console.log('Episode data received:', episode);
      
      // Manejar posibles inconsistencias entre nombres de campos
      const newFormData = {
        name: episode.name || episode.title || '',
        episodeNumber: episode.episodeNumber?.toString() || '',
        runtime: episode.runtime?.toString() || episode.duration?.toString() || '',
        description: episode.description || '',
        videoUrl: episode.videoUrl || episode.link || '',
        poster: episode.poster || episode.posterUrl || '',
        srtUrl: episode.srtUrl || episode.subtitleUrl || '',
        fileSize: episode.fileSize?.toString() || '',
        mimeType: episode.mimeType || '',
        seasonId: episode.seasonId?.toString() || ''
      };
      
      console.log('Setting episode form data:', newFormData);
      setFormData(newFormData);
    }
  }, [episode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convertir tipos de datos antes de enviar
    const dataToSave = {
      ...formData,
      episodeNumber: parseInt(formData.episodeNumber, 10) || 1,
      runtime: parseInt(formData.runtime, 10) || 20,
      seasonId: parseInt(formData.seasonId, 10),
      fileSize: formData.fileSize ? parseInt(formData.fileSize, 10) : null,
      // Para asegurar compatibilidad con ambos sistemas de nombres
      title: formData.name,
      duration: parseInt(formData.runtime, 10) || 20,
      posterUrl: formData.poster,
      subtitleUrl: formData.srtUrl,
      link: formData.videoUrl
    };
    
    console.log('Saving episode data:', dataToSave);
    onSave({ id: episode?.id, episodeData: dataToSave });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Editar Episodio</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white focus:outline-none"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="seasonId" className="block text-sm font-medium text-gray-300">
              Temporada
            </label>
            <select
              id="seasonId"
              name="seasonId"
              value={formData.seasonId}
              onChange={handleChange}
              required
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            >
              <option value="">Selecciona una temporada</option>
              {seasons.map(season => (
                <option key={season.id} value={season.id.toString()}>
                  {season.title} (Temporada {season.seasonNumber})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="episodeNumber" className="block text-sm font-medium text-gray-300">
                Número de Episodio
              </label>
              <input
                type="number"
                id="episodeNumber"
                name="episodeNumber"
                value={formData.episodeNumber}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="runtime" className="block text-sm font-medium text-gray-300">
                Duración (minutos)
              </label>
              <input
                type="number"
                id="runtime"
                name="runtime"
                value={formData.runtime}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300">
              Título
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="poster" className="block text-sm font-medium text-gray-300">
              URL del Póster
            </label>
            <input
              type="text"
              id="poster"
              name="poster"
              value={formData.poster}
              onChange={handleChange}
              placeholder="https://ejemplo.com/poster.jpg"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-300">
              URL de Video
            </label>
            <input
              type="text"
              id="videoUrl"
              name="videoUrl"
              value={formData.videoUrl}
              onChange={handleChange}
              placeholder="https://ejemplo.com/video.mp4"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="srtUrl" className="block text-sm font-medium text-gray-300">
              URL de Subtítulo
            </label>
            <input
              type="text"
              id="srtUrl"
              name="srtUrl"
              value={formData.srtUrl}
              onChange={handleChange}
              placeholder="https://ejemplo.com/subtitulos.vtt"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="fileSize" className="block text-sm font-medium text-gray-300">
                Tamaño del Archivo (KB)
              </label>
              <input
                type="number"
                id="fileSize"
                name="fileSize"
                value={formData.fileSize}
                onChange={handleChange}
                placeholder="1024"
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="mimeType" className="block text-sm font-medium text-gray-300">
                Tipo MIME
              </label>
              <input
                type="text"
                id="mimeType"
                name="mimeType"
                value={formData.mimeType}
                onChange={handleChange}
                placeholder="video/mp4"
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md font-medium text-white hover:bg-blue-700"
            >
              Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEpisodeModal;
